/**
 * @file Reads one project's detail from the `projectsByStage` answer the
 * Projects list already holds, so no request ever carries a project id.
 * Consumer: `AnalyticsProjectsPanel`, for the `project` in the URL. The base
 * row comes from `analyticsProjects`; an unanswered list passes through, and
 * a project missing from the answered rows is `absent`; a present one carries
 * the answer's granted level and period end. Each refused invoice
 * column refuses only its own figure, and `formValues` joins the form list by
 * form id. The invoiced share is an exact fraction, null unless the value is
 * positive. Any malformed detail cell fails the whole model.
 */
import { DateTime } from 'luxon';
import type { Snapshot } from 'valtio';

import {
  ANALYTICS_OPEN_PROJECT_STAGES,
  analyticsDecimalFraction,
} from '#types';
import type {
  AnalyticsFraction,
  NamedQueryCell,
  NamedQueryResult,
} from '#types';

import { analyticsJsonArray } from './analyticsJsonArray';
import { analyticsMalformedAnswer } from './analyticsMalformedAnswer';
import { analyticsProjects } from './analyticsProjects';
import { analyticsRefusedColumn } from './analyticsRefusedColumn';
import { analyticsUnansweredFigure } from './analyticsUnansweredFigure';
import type {
  AnalyticsProjectDetail,
  AnalyticsProjectForm,
} from './analyticsProjectsTab.types';
import type {
  AnalyticsAnswer,
  AnalyticsCountFigure,
  AnalyticsDecimalFigure,
} from './index.types';

type Row = Snapshot<Record<string, NamedQueryCell>>;

type Result = Snapshot<NamedQueryResult>;

const isTextOrNull = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

const isDecimal = (value: unknown): value is string =>
  typeof value === 'string' && analyticsDecimalFraction(value) !== undefined;

const isTimestampOrNull = (value: unknown): value is string | null =>
  value === null ||
  (typeof value === 'string' && DateTime.fromSQL(value).isValid);

const isRecord = (item: unknown): item is Record<string, unknown> =>
  typeof item === 'object' && item !== null && !Array.isArray(item);

const decimalFigure = (
  result: Result,
  row: Row,
  column: string,
): AnalyticsDecimalFigure | undefined => {
  const refused = analyticsRefusedColumn(result, column);
  if (refused) return { status: 'refusedColumn', column: refused };
  const cell = row[column];
  return isDecimal(cell) ? { status: 'answered', decimal: cell } : undefined;
};

const countFigure = (
  result: Result,
  row: Row,
  column: string,
): AnalyticsCountFigure | undefined => {
  const refused = analyticsRefusedColumn(result, column);
  if (refused) return { status: 'refusedColumn', column: refused };
  const cell = row[column];
  return typeof cell === 'number' && Number.isInteger(cell)
    ? { status: 'answered', count: cell }
    : undefined;
};

const formValuesOf = (
  cell: NamedQueryCell,
  isRefused: boolean,
): ReadonlyMap<unknown, string | null> | undefined => {
  if (isRefused) return new Map();
  const entries = analyticsJsonArray(cell, isRecord);
  const values = entries?.flatMap(({ formId, value }) =>
    value === null || isDecimal(value) ? [[formId, value] as const] : [],
  );
  return values?.length === entries?.length ? new Map(values) : undefined;
};

const formOf = (
  { formId, formNumber, stage, state, updatedAt }: Record<string, unknown>,
  values: ReadonlyMap<unknown, string | null>,
): AnalyticsProjectForm | undefined => {
  const open = ANALYTICS_OPEN_PROJECT_STAGES.find((s) => s === stage);
  return typeof formId === 'string' &&
    open !== undefined &&
    isTextOrNull(formNumber) &&
    isTextOrNull(state) &&
    isTimestampOrNull(updatedAt)
    ? {
        formId,
        formNumber,
        stage: open,
        state,
        value: values.get(formId) ?? null,
        updatedAt,
      }
    : undefined;
};

const formsOf = (
  cell: NamedQueryCell,
  values: ReadonlyMap<unknown, string | null>,
): readonly AnalyticsProjectForm[] | undefined => {
  const entries = analyticsJsonArray(cell, isRecord);
  const forms = entries?.flatMap((entry) => formOf(entry, values) ?? []);
  return forms?.length === entries?.length ? forms : undefined;
};

const invoicedShareOf = (
  invoiced: AnalyticsDecimalFigure,
  value: string | null,
): AnalyticsFraction | null => {
  const part =
    invoiced.status === 'answered'
      ? analyticsDecimalFraction(invoiced.decimal)
      : undefined;
  const whole = value === null ? undefined : analyticsDecimalFraction(value);
  return part && whole && whole.numerator > 0n
    ? {
        numerator: part.numerator * whole.denominator,
        denominator: part.denominator * whole.numerator,
      }
    : null;
};

export const analyticsProjectDetail = (
  answer: Snapshot<AnalyticsAnswer> | undefined,
  projectId: string,
): AnalyticsProjectDetail => {
  if (answer?.status !== 'answered') return analyticsUnansweredFigure(answer);
  const projects = analyticsProjects(answer);
  if (projects.status !== 'answered') return projects;
  const { result } = answer;
  const row = projects.rows.find((r) => r.projectId === projectId);
  const cells = result.rows.find((r) => r.projectId === projectId);
  if (row === undefined || cells === undefined) return { status: 'absent' };
  const refusedDueDate = analyticsRefusedColumn(
    result,
    'oldestOutstandingDueDate',
  );
  const refusedFormValue = analyticsRefusedColumn(result, 'formValues');
  const values = formValuesOf(cells.formValues, refusedFormValue !== null);
  const forms = values && formsOf(cells.formList, values);
  const invoiced = decimalFigure(result, cells, 'invoicedAmount');
  const outstanding = decimalFigure(result, cells, 'outstandingAmount');
  const outstandingInvoices = countFigure(result, cells, 'outstandingInvoices');
  const { oldestOutstandingDueDate, formListTotal } = cells;
  const valid =
    forms !== undefined &&
    invoiced !== undefined &&
    outstanding !== undefined &&
    outstandingInvoices !== undefined &&
    isTimestampOrNull(oldestOutstandingDueDate) &&
    typeof formListTotal === 'number' &&
    Number.isInteger(formListTotal);
  if (!valid) return analyticsMalformedAnswer('projectsByStage');
  return {
    status: 'answered',
    level: result.level,
    to: result.period.to,
    row,
    refusedValue: projects.refusedValue,
    invoiced,
    invoicedShare: invoicedShareOf(invoiced, row.value),
    outstanding,
    outstandingInvoices,
    oldestOutstandingDueDate,
    refusedDueDate,
    forms,
    formListTotal,
    refusedFormValue,
  };
};
